import React, { useEffect, useState } from "react";
import SidebarOrganizer from "../../components/Sidebar/SidebarOrganizer"; // ✅ Import Sidebar
import { db, auth } from "../../firebase";
import {
  collection,
  addDoc,
  onSnapshot,
  updateDoc,
  doc,
  deleteDoc,
  increment,
  getDocs,
  getDoc,
  serverTimestamp,
  query,
  where,
} from "firebase/firestore";
import { getGroupFinancialSummary } from "../../utils/chitFundCalculations";
import "./OrganizerDashboard.css";

export default function OrganizerDashboard() {
  const [groups, setGroups] = useState([]);
  const [requests, setRequests] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [contribution, setContribution] = useState("");
  const [maxMembers, setMaxMembers] = useState("");
  const [months, setMonths] = useState("");
  const [commission, setCommission] = useState("");
  const [totalMoney, setTotalMoney] = useState("");
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [selectedGroupForAdd, setSelectedGroupForAdd] = useState(null);
  const [memberData, setMemberData] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [userName, setUserName] = useState("");
  const [activeTab, setActiveTab] = useState('groups'); // 'groups' or 'requests'
  const [showAllGroups, setShowAllGroups] = useState(false);
  
  useEffect(() => {
    if (!auth.currentUser) return;
  
    const user = auth.currentUser;
  
    if (user.displayName) {
      setUserName(user.displayName);
    } else if (user.email) {
      setUserName(user.email.split("@")[0]);
    }
  }, []);

  useEffect(() => {
    if (!auth.currentUser) return;

    const currentUserId = auth.currentUser.uid;

    // Fetch groups with members - filtered by organizerId
    const groupsQuery = query(collection(db, "groups"), where("organizerId", "==", currentUserId));
    const unsubGroups = onSnapshot(groupsQuery, async (snap) => {
      const groupsData = await Promise.all(
        snap.docs.map(async (g) => {
          const membersSnap = await getDocs(collection(db, `groups/${g.id}/members`));
          const members = membersSnap.docs.map((m) => ({ id: m.id, ...m.data() }));
          return { id: g.id, ...g.data(), members };
        })
      );
      setGroups(groupsData);
    });

    return () => {
      unsubGroups();
    };
  }, []);

  // Filter join requests by organizer's groups
  useEffect(() => {
    if (groups.length === 0) {
      setRequests([]);
      return;
    }

    const organizerGroupIds = groups.map((g) => g.id);
    const unsubReq = onSnapshot(collection(db, "joinRequests"), (reqSnap) => {
      const allRequests = reqSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Filter requests that belong to organizer's groups
      const filteredRequests = allRequests.filter((req) => 
        organizerGroupIds.includes(req.groupId)
      );
      setRequests(filteredRequests);
    });

    return () => {
      unsubReq();
    };
  }, [groups]);

  const createGroup = async () => {
    if (!groupName || !contribution || !maxMembers || !months || !commission || !totalMoney) {
      alert("Please fill all fields");
      return;
    }

    // Validate that totalMoney matches (maxMembers × contribution)
    const calculatedTotal = parseInt(maxMembers) * parseInt(contribution);
    if (parseInt(totalMoney) !== calculatedTotal) {
      alert(`Total money (₹${totalMoney}) should equal Members × Contribution (${maxMembers} × ₹${contribution} = ₹${calculatedTotal})`);
      return;
    }

    try {
      const currentUser = auth.currentUser;
      
      console.log("👤 Creating group with organizerId:", currentUser?.uid);
      await addDoc(collection(db, "groups"), {
        groupName,
        contribution: parseInt(contribution),
        maxMembers: parseInt(maxMembers),
        months: parseInt(months),
        commission: parseInt(commission),
        totalMoney: parseInt(totalMoney),
        currentMembers: 0,
        organizerId: currentUser?.uid,
        currentMonth: 1, // Initialize to month 1
        createdAt: new Date(),
      });
      console.log("✅ Group created with organizerId:", currentUser?.uid);
      setGroupName("");
      setContribution("");
      setMaxMembers("");
      setMonths("");
      setCommission("");
      setTotalMoney("");
      alert("✅ Group created successfully!");
    } catch (err) {
      console.error("Error creating group:", err);
      alert("Failed to create group: " + err.message);
    }
  };

  const handleAccept = async (req) => {
    try {
      if (!req || !req.id) {
        alert("❌ Invalid request");
        return;
      }

      const group = groups.find(g => g.id === req.groupId);
      if (!group) {
        alert("❌ Group not found");
        return;
      }

      // Add member to group
      console.log("➕ Adding member to group...");
      await addDoc(collection(db, `groups/${group.id}/members`), {
        name: req.memberName || "Unknown",
        email: req.memberEmail || "unknown@email.com",
        phone: req.memberPhone || "0000000000",
        memberId: req.memberId,
        joinedAt: new Date(),
        paid: false,
        hasWon: false, // Initialize hasWon to false
      });

      // Update request status
      console.log("🔄 Updating request status to accepted...");
      await updateDoc(doc(db, "joinRequests", req.id), {
        status: "accepted",
        respondedAt: new Date(),
      });

      // Increment group member count
      console.log("📊 Incrementing group member count...");
      await updateDoc(doc(db, "groups", group.id), {
        currentMembers: increment(1),
      });

      // Send notification to member
      if (req.memberId) {
        console.log("📧 Sending notification to member...");
        await addDoc(collection(db, "notifications"), {
          to: req.memberId,
          type: "request_response",
          message: `✅ Organizer accepted your request to join ${group.groupName}!`,
          groupId: group.id,
          createdAt: new Date(),
          read: false,
        });
        console.log("✅ Notification sent!");
      }

      alert("✅ Request accepted! Notification sent to member.");
    } catch (err) {
      console.error("Error accepting request:", err);
      alert("Failed to accept request: " + err.message);
    }
  };

  const handleReject = async (req) => {
    try {
      if (!req || !req.id) {
        alert("❌ Invalid request");
        return;
      }

      const group = groups.find(g => g.id === req.groupId);

      console.log("❌ Rejecting request...");
      await updateDoc(doc(db, "joinRequests", req.id), {
        status: "rejected",
        respondedAt: new Date(),
      });

      // Send rejection notification
      if (req.memberId && group) {
        console.log("📧 Sending rejection notification to member...");
        await addDoc(collection(db, "notifications"), {
          to: req.memberId,
          type: "request_response",
          message: `❌ Your request to join ${group.groupName} was rejected.`,
          groupId: group.id,
          createdAt: new Date(),
          read: false,
        });
      }

      alert("🔴 Request rejected!");
    } catch (err) {
      console.error("Error rejecting request:", err);
      alert("Failed to reject request: " + err.message);
    }
  };

  // Toggle group expansion
  const toggleGroup = (groupId) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
      setSelectedGroupForAdd(null);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  // Toggle add member form
  const toggleAddMember = (groupId) => {
    setSelectedGroupForAdd(selectedGroupForAdd === groupId ? null : groupId);
    setMemberData({ name: "", email: "", phone: "" });
  };

  // Handle member input change
  const handleMemberInputChange = (e) => {
    setMemberData({ ...memberData, [e.target.name]: e.target.value });
  };

  // Add member to group
  const handleAddMember = async (groupId) => {
    const { name, email, phone } = memberData;

    if (!name || !email || !phone) {
      alert("Please fill all fields");
      return;
    }
    if (!email.includes("@")) {
      alert("Email must contain '@'");
      return;
    }
    if (phone.length !== 10) {
      alert("Phone number must be exactly 10 digits");
      return;
    }

    const group = groups.find(g => g.id === groupId);
    if (group && group.maxMembers && (group.currentMembers || 0) >= group.maxMembers) {
      alert("❌ Group is full!");
      return;
    }

    try {
      // Add member to members subcollection
      await addDoc(collection(db, `groups/${groupId}/members`), {
        name,
        email,
        phone,
        joinedAt: serverTimestamp(),
        paid: false,
        hasWon: false, // Initialize hasWon to false
      });

      // Increment currentMembers count
      const groupRef = doc(db, "groups", groupId);
      await updateDoc(groupRef, {
        currentMembers: increment(1),
      });

      alert("✅ Member added successfully!");
      setSelectedGroupForAdd(null);
      setMemberData({ name: "", email: "", phone: "" });
    } catch (error) {
      console.error("Error adding member:", error);
      alert("Failed to add member. Please try again.");
    }
  };

  // Delete member from group (BUG FIX: decrements currentMembers and updates joinRequest)
  const handleDeleteMember = async (groupId, memberId) => {
    if (!window.confirm("Are you sure you want to remove this member?")) {
      return;
    }

    try {
      // Get member data to find their memberId before deleting
      const memberRef = doc(db, `groups/${groupId}/members/${memberId}`);
      const memberSnap = await getDoc(memberRef);
      const memberData = memberSnap.data();
      const memberUserId = memberData?.memberId;

      // Delete member document
      await deleteDoc(doc(db, `groups/${groupId}/members/${memberId}`));

      // Decrement currentMembers count
      const groupRef = doc(db, "groups", groupId);
      await updateDoc(groupRef, {
        currentMembers: increment(-1),
      });

      // Update joinRequest status to "removed" so member side reflects the removal
      if (memberUserId) {
        const group = groups.find(g => g.id === groupId);
        const joinRequestsSnap = await getDocs(
          query(
            collection(db, "joinRequests"),
            where("groupId", "==", groupId),
            where("memberId", "==", memberUserId)
          )
        );
        
        if (!joinRequestsSnap.empty) {
          const joinRequestDoc = joinRequestsSnap.docs[0];
          await updateDoc(doc(db, "joinRequests", joinRequestDoc.id), {
            status: "removed",
            removedAt: serverTimestamp(),
          });
          console.log("✅ Join request status updated to 'removed'");

          // Send notification to member about removal
          if (group) {
            await addDoc(collection(db, "notifications"), {
              to: memberUserId,
              type: "member_removed",
              message: `You have been removed from the group "${group.groupName}" by the organizer.`,
              groupId: groupId,
              createdAt: serverTimestamp(),
              read: false,
            });
            console.log("✅ Removal notification sent to member");
          }
        }
      }

      alert("✅ Member removed successfully! Group count updated and member notified.");
    } catch (error) {
      console.error("Error deleting member:", error);
      alert("Failed to remove member. Please try again.");
    }
  };

  return (
    <div className="dashboard-layout">
      <SidebarOrganizer /> {/* ✅ Sidebar is here */}
      <div className="organizer-dashboard">
        <div className="dashboard-top-bar">
          <div className="welcome-compact">
            <h1>Welcome, {userName} 👋</h1>
            <p>{activeTab === 'groups' ? `${groups.length} groups` : `${requests.filter(r => r.status === 'processing' || r.status === 'pending').length} pending requests`}</p>
          </div>
        </div>

        <div className="tabs-container">
          <button 
            className={`tab-button ${activeTab === 'groups' ? 'active' : ''}`}
            onClick={() => setActiveTab('groups')}
          >
            My Groups ({groups.length})
          </button>
          <button 
            className={`tab-button ${activeTab === 'requests' ? 'active' : ''}`}
            onClick={() => setActiveTab('requests')}
          >
            Join Requests ({requests.filter(r => r.status === 'processing' || r.status === 'pending').length})
          </button>
        </div>

        {activeTab === 'groups' && (
          <>
        <div className="create-group-box" style={{
          background: '#ffffff',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          marginBottom: '20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              fontWeight: '700',
              color: 'white',
            }}>
              +
            </div>
            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: '#111827' }}>Create New Group</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
            <input
              type="text"
              placeholder="Group Name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              style={{
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '15px',
                transition: 'all 0.2s',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#2563eb';
                e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            />
            <input
              type="number"
              placeholder="Max Members"
              value={maxMembers}
              onChange={(e) => {
                setMaxMembers(e.target.value);
                // Auto-calculate total money when members or contribution changes
                if (contribution && e.target.value) {
                  setTotalMoney((parseInt(contribution) * parseInt(e.target.value)).toString());
                }
              }}
              style={{
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '15px',
                transition: 'all 0.2s',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#2563eb';
                e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            />
            <input
              type="number"
              placeholder="Monthly Contribution (₹)"
              value={contribution}
              onChange={(e) => {
                setContribution(e.target.value);
                // Auto-calculate total money when members or contribution changes
                if (maxMembers && e.target.value) {
                  setTotalMoney((parseInt(e.target.value) * parseInt(maxMembers)).toString());
                }
              }}
              style={{
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '15px',
                transition: 'all 0.2s',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#2563eb';
                e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            />
            <input
              type="number"
              placeholder="Total Money (₹)"
              value={totalMoney}
              onChange={(e) => setTotalMoney(e.target.value)}
              style={{
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '15px',
                transition: 'all 0.2s',
                outline: 'none',
                backgroundColor: '#f9fafb',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#2563eb';
                e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                e.target.style.backgroundColor = '#fff';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
                e.target.style.backgroundColor = '#f9fafb';
              }}
            />
            <input
              type="number"
              placeholder="Duration (Months)"
              value={months}
              onChange={(e) => setMonths(e.target.value)}
              style={{
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '15px',
                transition: 'all 0.2s',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#2563eb';
                e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            />
            <input
              type="number"
              placeholder="Monthly Commission (₹)"
              value={commission}
              onChange={(e) => setCommission(e.target.value)}
              style={{
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '15px',
                transition: 'all 0.2s',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#2563eb';
                e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          {totalMoney && maxMembers && contribution && (
            <div style={{
              padding: '12px',
              backgroundColor: '#eff6ff',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#1e40af',
            }}>
              💡 Total Money: ₹{totalMoney} = {maxMembers} Members × ₹{contribution} per month
            </div>
          )}
          <button 
            onClick={createGroup}
            style={{
              padding: '12px 24px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              transition: 'all 0.2s',
              boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#1d4ed8';
              e.target.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.4)';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#2563eb';
              e.target.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.3)';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            Create Group
          </button>
        </div>

        <div className="group-container">
          {groups.length === 0 && (
            <p className="empty-state">No groups created yet.</p>
          )}
          {(showAllGroups ? groups : groups.slice(0, 6)).map((g) => {
            const isExpanded = expandedGroups.has(g.id);
            const members = g.members || [];
            const isFull = (g.currentMembers || 0) >= (g.maxMembers || 0);
            
            // Calculate financial summary
            const financialSummary = getGroupFinancialSummary(g, members);
            const previousWinners = members.filter(m => m.hasWon === true);
            
            return (
              <div key={g.id} className="group-card" style={{ 
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                border: '1px solid #e5e7eb',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)';
              }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      <div style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '700',
                        fontSize: '18px',
                      }}>
                        {g.groupName ? g.groupName.charAt(0).toUpperCase() : 'G'}
                      </div>
                      <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>{g.groupName}</h3>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginLeft: '56px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '14px', color: '#6b7280' }}>Month:</span>
                        <strong style={{ fontSize: '16px', color: '#2563eb' }}>{financialSummary.currentMonth}</strong>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '14px', color: '#6b7280' }}>Contribution:</span>
                        <strong style={{ fontSize: '16px', color: '#2563eb' }}>₹{g.contribution || 0}</strong>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '14px', color: '#6b7280' }}>Members:</span>
                        <strong style={{ fontSize: '16px', color: '#111827' }}>{g.currentMembers || 0} / {g.maxMembers || 0}</strong>
                        {isFull && (
                          <span style={{ 
                            marginLeft: '8px', 
                            padding: '4px 10px', 
                            backgroundColor: '#fef2f2', 
                            color: '#dc2626', 
                            borderRadius: '12px', 
                            fontSize: '11px', 
                            fontWeight: '700',
                            border: '1px solid #fecaca',
                          }}>
                            FULL
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleGroup(g.id)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: isExpanded ? '#6b7280' : '#2563eb',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                    }}
                  >
                    {isExpanded ? '▲ Hide' : '▼ View'}
                  </button>
                </div>

                {isExpanded && (
                  <div style={{ 
                    marginTop: '20px', 
                    paddingTop: '20px', 
                    borderTop: '2px solid #e5e7eb',
                    animation: 'fadeIn 0.3s ease',
                  }}>
                    {/* Financial Summary */}
                    <div style={{
                      marginBottom: '20px',
                      padding: '16px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '12px',
                      border: '1px solid #e5e7eb',
                    }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '700', color: '#111827' }}>
                        📊 Financial Summary (Month {financialSummary.currentMonth})
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '12px' }}>
                        <div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Monthly Pool</div>
                          <div style={{ fontSize: '18px', fontWeight: '700', color: '#2563eb' }}>₹{financialSummary.monthlyPool}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Organizer Commission</div>
                          <div style={{ fontSize: '18px', fontWeight: '700', color: '#059669' }}>₹{financialSummary.organizerCommission}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Winner Payout</div>
                          <div style={{ fontSize: '18px', fontWeight: '700', color: '#dc2626' }}>₹{financialSummary.winnerPayout}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Previous Winners</div>
                          <div style={{ fontSize: '18px', fontWeight: '700', color: '#7c3aed' }}>{financialSummary.previousWinnersCount}</div>
                        </div>
                      </div>
                      {previousWinners.length > 0 && (
                        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>Members paying extra (₹1,000 penalty):</div>
                          <div style={{ fontSize: '13px', color: '#111827' }}>
                            {previousWinners.map(m => m.name).join(', ')}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Add Member Button */}
                    {!isFull && (
                      <button
                        onClick={() => toggleAddMember(g.id)}
                        style={{
                          width: '100%',
                          padding: '10px',
                          backgroundColor: selectedGroupForAdd === g.id ? '#6b7280' : '#2563eb',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          marginBottom: '16px',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          if (selectedGroupForAdd !== g.id) {
                            e.target.style.backgroundColor = '#1d4ed8';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedGroupForAdd !== g.id) {
                            e.target.style.backgroundColor = '#2563eb';
                          }
                        }}
                      >
                        {selectedGroupForAdd === g.id ? '✕ Cancel' : '+ Add Member'}
                      </button>
                    )}

                    {/* Add Member Form */}
                    {selectedGroupForAdd === g.id && !isFull && (
                      <div style={{
                        padding: '16px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        border: '1px solid #e5e7eb',
                      }}>
                        <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: '600' }}>Add New Member</h4>
                        <input
                          type="text"
                          name="name"
                          placeholder="Member Name"
                          value={memberData.name}
                          onChange={handleMemberInputChange}
                          style={{
                            width: '100%',
                            padding: '10px',
                            marginBottom: '10px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            boxSizing: 'border-box',
                          }}
                        />
                        <input
                          type="email"
                          name="email"
                          placeholder="Email"
                          value={memberData.email}
                          onChange={handleMemberInputChange}
                          style={{
                            width: '100%',
                            padding: '10px',
                            marginBottom: '10px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            boxSizing: 'border-box',
                          }}
                        />
                        <input
                          type="tel"
                          name="phone"
                          placeholder="Phone (10 digits)"
                          value={memberData.phone}
                          onChange={handleMemberInputChange}
                          style={{
                            width: '100%',
                            padding: '10px',
                            marginBottom: '12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            boxSizing: 'border-box',
                          }}
                        />
                        <button
                          onClick={() => handleAddMember(g.id)}
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: '#2563eb',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                          }}
                        >
                          Add Member
                        </button>
                      </div>
                    )}

                    {/* Members List */}
                    <div style={{ marginTop: '20px' }}>
                      <h4 style={{ 
                        margin: '0 0 16px 0', 
                        fontSize: '16px', 
                        fontWeight: '700', 
                        color: '#111827',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                        <span>Members</span>
                        <span style={{
                          padding: '2px 10px',
                          backgroundColor: '#eff6ff',
                          color: '#2563eb',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                        }}>
                          {members.length}
                        </span>
                      </h4>
                      {members.length === 0 ? (
                        <div style={{ 
                          color: '#6b7280', 
                          fontSize: '14px', 
                          padding: '24px', 
                          backgroundColor: '#f9fafb', 
                          borderRadius: '12px',
                          textAlign: 'center',
                          border: '2px dashed #e5e7eb',
                        }}>
                          <div style={{ 
                            width: '48px',
                            height: '48px',
                            margin: '0 auto 12px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '24px',
                          }}>👤</div>
                          <p style={{ margin: 0, fontWeight: '500' }}>No members yet</p>
                          <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>Add your first member above</p>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {members.map((member) => (
                            <div
                              key={member.id}
                              style={{
                                padding: '16px',
                                background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                                borderRadius: '12px',
                                border: '1px solid #e5e7eb',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                transition: 'all 0.2s',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#2563eb';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#e5e7eb';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontWeight: '700',
                                  fontSize: '16px',
                                }}>
                                  {member.name ? member.name.charAt(0).toUpperCase() : '?'}
                                </div>
                                <div>
                                  <p style={{ margin: '0 0 4px 0', fontWeight: '600', fontSize: '15px', color: '#111827' }}>
                                    {member.name || 'Unnamed'}
                                  </p>
                                  <p style={{ margin: '2px 0', fontSize: '13px', color: '#6b7280' }}>
                                    📧 {member.email || 'No email'}
                                  </p>
                                  <p style={{ margin: '2px 0', fontSize: '13px', color: '#6b7280' }}>
                                    📱 {member.phone || 'No phone'}
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={() => handleDeleteMember(g.id, member.id)}
                                style={{
                                  padding: '8px 16px',
                                  backgroundColor: '#dc2626',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  marginLeft: '12px',
                                  transition: 'all 0.2s',
                                  boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)',
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = '#b91c1c';
                                  e.target.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = '#dc2626';
                                  e.target.style.transform = 'scale(1)';
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {groups.length > 6 && (
          <div className="show-more-container">
            <button className="show-more-btn" onClick={() => setShowAllGroups(!showAllGroups)}>
              {showAllGroups ? 'Show Less' : `Show All (${groups.length - 6} more)`}
            </button>
          </div>
        )}
          </>
        )}

        {activeTab === 'requests' && (
          <div className="requests-box">
            {requests.filter(r => r.status === 'pending' || r.status === 'processing').length === 0 ? (
              <p className="empty-state">No pending requests</p>
            ) : (
              requests
                .filter(r => r.status === 'pending' || r.status === 'processing')
                .map((req) => (
                  <div key={req.id} className="request-card" style={{ borderLeft: '4px solid #f59e0b' }}>
                    <div className="request-info">
                      <p><strong>{req.memberName || 'Unknown'}</strong> requested to join <b>{req.groupName}</b></p>
                      <p style={{ margin: '4px 0', fontSize: '12px', color: '#666' }}>
                        Status: <span style={{ background: req.status === 'processing' ? '#fff3cd' : '#e7f3ff', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
                          {req.status === 'processing' ? 'Processing' : 'Pending'}
                        </span>
                      </p>
                    </div>
                    <div className="request-actions">
                      <button className="accept-btn" onClick={() => handleAccept(req)}>
                        Accept
                      </button>
                      <button className="reject-btn" onClick={() => handleReject(req)}>
                        Reject
                      </button>
                    </div>
                  </div>
                ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
